<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  pipeline: {
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  getSlaInfo: {
    type: Function,
    required: true,
  },
  getChannelInfo: {
    type: Function,
    required: true,
  },
  getSourceInfo: {
    type: Function,
    required: true,
  },
  getTeamInfo: {
    type: Function,
    required: true,
  },
  getDealValue: {
    type: Function,
    required: true,
  },
  getDealStatus: {
    type: Function,
    required: true,
  },
  formatCurrency: {
    type: Function,
    required: true,
  },
  formatDateTime: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits([
  'move-stage',
  'assign-me',
  'resolve',
  'update-deal-value',
  'update-deal-status',
  'rename-lead',
]);

const { t } = useI18n();
const router = useRouter();

const editingValueId = ref(null);
const editingValueInput = ref(0);

const editingNameId = ref(null);
const editingNameInput = ref('');

function getCurrentStageId(conversation) {
  const stages = props.pipeline.stages || [];
  const labels = conversation.labels || [];

  if (conversation.status === 'resolved') {
    const resolveStage = stages.find(s => s.action === 'resolve');
    if (resolveStage) return resolveStage.id;
  }

  const matched = stages.find(
    s =>
      (s.label && labels.includes(s.label)) || (s.id && labels.includes(s.id))
  );
  if (matched) return matched.id;

  if (conversation.meta?.assignee && stages[1]) return stages[1].id;
  return stages[0]?.id || '';
}

function handleStageSelect(conversationId, event) {
  const targetStageId = event.target.value;
  if (targetStageId) {
    emit('move-stage', conversationId, targetStageId);
  }
}

function openConversation(conversation) {
  const accountId = conversation.account_id;
  const id = conversation.id;
  if (accountId && id) {
    router
      .push({
        name: 'inbox_conversation',
        params: { accountId, conversation_id: id },
      })
      .catch(() => {
        window.location.href = `/app/accounts/${accountId}/conversations/${id}`;
      });
  }
}

function startEditValue(conversation) {
  editingValueId.value = conversation.id;
  editingValueInput.value = props.getDealValue(conversation);
}

function saveValueEdit(conversationId) {
  editingValueId.value = null;
  emit('update-deal-value', conversationId, editingValueInput.value);
}

function startEditName(conversation) {
  editingNameId.value = conversation.id;
  editingNameInput.value = conversation.meta?.sender?.name || '';
}

function saveNameEdit(conversationId) {
  editingNameId.value = null;
  if (editingNameInput.value.trim()) {
    emit('rename-lead', conversationId, editingNameInput.value.trim());
  }
}

function setWonStatus(conversation) {
  const current = props.getDealStatus(conversation);
  const next = current === 'won' ? 'open' : 'won';
  emit('update-deal-status', conversation.id, next);
}

function setLostStatus(conversation) {
  const current = props.getDealStatus(conversation);
  const next = current === 'lost' ? 'open' : 'lost';
  emit('update-deal-status', conversation.id, next);
}
</script>

<template>
  <main class="flex-1 overflow-auto p-6 bg-n-surface-1">
    <div
      class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm overflow-hidden"
    >
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse min-w-[1100px]">
          <thead>
            <tr
              class="border-b border-n-weak bg-n-surface-1/80 text-[11px] font-extrabold uppercase tracking-wider text-n-slate-11"
            >
              <th class="py-3 px-4 w-16 text-center">Status</th>
              <th class="py-3 px-4 min-w-[220px]">Lead / Negócio</th>
              <th class="py-3 px-4 min-w-[170px]">Etapa do Funil</th>
              <th class="py-3 px-4 min-w-[160px]">Atendente</th>
              <th class="py-3 px-4 min-w-[110px]">Time / Setor</th>
              <th class="py-3 px-4 min-w-[150px]">Fonte / Origem</th>
              <th class="py-3 px-4 min-w-[130px] text-right">Valor (R$)</th>
              <th class="py-3 px-4 min-w-[170px]">Entrada & Espera</th>
              <th class="py-3 px-4 w-20 text-center">Ações</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak/60 text-xs">
            <tr
              v-for="conversation in conversations"
              :key="conversation.id"
              class="group hover:bg-n-surface-2/60 transition-colors"
              :class="[
                (conversation.unread_count || 0) > 0 ? 'bg-woot-500/5' : '',
                getDealStatus(conversation) === 'won' ? 'bg-emerald-500/5' : '',
                getDealStatus(conversation) === 'lost' ? 'bg-red-500/5' : '',
              ]"
            >
              <!-- 1. Status Indicator Dots (Green = Won, Red = Lost) -->
              <td class="py-3 px-4 text-center">
                <div class="flex items-center justify-center gap-1.5">
                  <button
                    type="button"
                    class="flex size-5 items-center justify-center rounded-full border transition-all hover:scale-110 cursor-pointer"
                    :class="[
                      getDealStatus(conversation) === 'won'
                        ? 'bg-emerald-500 border-emerald-600 text-white ring-2 ring-emerald-500/30'
                        : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-600 hover:bg-emerald-500 hover:text-white',
                    ]"
                    title="Marcar como Ganho"
                    @click="setWonStatus(conversation)"
                  >
                    <span class="text-[10px] font-bold">✓</span>
                  </button>

                  <button
                    type="button"
                    class="flex size-5 items-center justify-center rounded-full border transition-all hover:scale-110 cursor-pointer"
                    :class="[
                      getDealStatus(conversation) === 'lost'
                        ? 'bg-red-500 border-red-600 text-white ring-2 ring-red-500/30'
                        : 'bg-red-500/20 border-red-500/40 text-red-600 hover:bg-red-500 hover:text-white',
                    ]"
                    title="Marcar como Perdido"
                    @click="setLostStatus(conversation)"
                  >
                    <span class="text-[10px] font-bold">✕</span>
                  </button>
                </div>
              </td>

              <!-- 2. Lead Name & Contact Info -->
              <td class="py-3 px-4">
                <div class="flex items-center gap-2.5">
                  <Avatar
                    :name="conversation.meta?.sender?.name || 'Contato'"
                    :src="conversation.meta?.sender?.thumbnail || conversation.meta?.sender?.avatar_url || ''"
                    size="28"
                    class="rounded-full border border-n-weak flex-shrink-0"
                  />
                  <div class="flex flex-col min-w-0 flex-1">
                    <div class="flex items-center gap-1.5 group/name">
                      <template v-if="editingNameId === conversation.id">
                        <input
                          v-model="editingNameInput"
                          type="text"
                          class="w-full rounded border border-woot-500 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-xs font-bold text-n-slate-12 focus:outline-none"
                          @keyup.enter="saveNameEdit(conversation.id)"
                          @blur="saveNameEdit(conversation.id)"
                        />
                      </template>
                      <template v-else>
                        <span class="truncate font-extrabold text-n-slate-12">
                          {{
                            conversation.meta?.sender?.name ||
                            'Contato Sem Nome'
                          }}
                        </span>
                        <button
                          type="button"
                          class="opacity-0 group-hover/name:opacity-100 p-0.5 text-n-slate-10 hover:text-woot-500 cursor-pointer"
                          title="Renomear Lead"
                          @click="startEditName(conversation)"
                        >
                          <Icon icon="i-lucide-pencil" class="size-3" />
                        </button>
                      </template>

                      <!-- Unread Ping Badge -->
                      <span
                        v-if="(conversation.unread_count || 0) > 0"
                        class="size-2 rounded-full bg-woot-500 animate-ping"
                        title="Nova mensagem"
                      />
                    </div>

                    <span
                      class="truncate text-[11px] text-n-slate-10 font-medium"
                    >
                      {{
                        conversation.meta?.sender?.phone_number ||
                        conversation.meta?.sender?.email ||
                        `#${conversation.display_id}`
                      }}
                    </span>
                  </div>
                </div>
              </td>

              <!-- 3. Pipeline Stage Selector -->
              <td class="py-3 px-4">
                <select
                  :value="getCurrentStageId(conversation)"
                  class="w-full h-8 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-xs font-semibold text-n-slate-12 shadow-2xs focus:border-n-brand focus:outline-none cursor-pointer"
                  @change="handleStageSelect(conversation.id, $event)"
                >
                  <option
                    v-for="stage in pipeline.stages"
                    :key="stage.id"
                    :value="stage.id"
                  >
                    {{ stage.name }}
                  </option>
                </select>
              </td>

              <!-- 4. Assignee Agent -->
              <td class="py-3 px-4">
                <div class="flex items-center gap-1.5">
                  <template v-if="conversation.meta?.assignee">
                    <Avatar
                      :name="conversation.meta.assignee.name"
                      :src="conversation.meta.assignee.thumbnail || conversation.meta.assignee.avatar_url || ''"
                      size="22"
                      class="rounded-full border border-n-weak flex-shrink-0"
                    />
                    <span
                      class="truncate font-semibold text-n-slate-11 max-w-[110px]"
                    >
                      {{ conversation.meta.assignee.name }}
                    </span>
                  </template>
                  <template v-else>
                    <button
                      type="button"
                      class="flex items-center gap-1 rounded-full bg-n-surface-2 hover:bg-woot-500/10 px-2 py-0.5 text-[10px] font-semibold text-n-slate-10 hover:text-woot-500 border border-n-weak transition-colors cursor-pointer"
                      @click="$emit('assign-me', conversation.id)"
                    >
                      <Icon icon="i-lucide-user-plus" class="size-3" />
                      <span>Atribuir</span>
                    </button>
                  </template>
                </div>
              </td>

              <!-- 5. Team / Department Tag -->
              <td class="py-3 px-4">
                <span
                  v-if="getTeamInfo(conversation)"
                  class="inline-flex items-center rounded-md bg-blue-500/10 px-2 py-0.5 text-[11px] font-semibold text-blue-600 border border-blue-500/20"
                >
                  {{ getTeamInfo(conversation) }}
                </span>
                <span v-else
class="text-n-slate-10 text-[11px] font-normal"
                  >—</span>
              </td>

              <!-- 6. Source / Origin (Icon + "Fonte: Google Ads" Text) -->
              <td class="py-3 px-4">
                <div
                  class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold border shadow-2xs"
                  :class="getSourceInfo(conversation).colorClass"
                >
                  <Icon
                    :icon="getSourceInfo(conversation).icon"
                    class="size-3.5 flex-shrink-0"
                  />
                  <span class="truncate">Fonte: {{ getSourceInfo(conversation).name }}</span>
                </div>
              </td>

              <!-- 7. Editable Deal Value (R$) -->
              <td
                class="py-3 px-4 text-right font-extrabold text-emerald-600 dark:text-emerald-400"
              >
                <template v-if="editingValueId === conversation.id">
                  <input
                    v-model="editingValueInput"
                    type="number"
                    step="0.01"
                    class="w-24 rounded border border-woot-500 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-xs text-right font-bold text-n-slate-12 focus:outline-none"
                    @keyup.enter="saveValueEdit(conversation.id)"
                    @blur="saveValueEdit(conversation.id)"
                  />
                </template>
                <template v-else>
                  <button
                    type="button"
                    class="hover:bg-woot-500/10 px-1.5 py-0.5 rounded transition-colors cursor-pointer"
                    title="Clique para editar o valor"
                    @click="startEditValue(conversation)"
                  >
                    {{ formatCurrency(getDealValue(conversation)) }}
                  </button>
                </template>
              </td>

              <!-- 8. Entry Date/Time & SLA Wait Time -->
              <td class="py-3 px-4">
                <div class="flex flex-col gap-0.5 text-[11px]">
                  <span class="font-medium text-n-slate-12">
                    {{ formatDateTime(conversation.created_at) || 'N/A' }}
                  </span>
                  <span class="text-[10px] text-n-slate-10">
                    Espera: {{ getSlaInfo(conversation).formattedTime }}
                  </span>
                </div>
              </td>

              <!-- 9. Quick Actions -->
              <td class="py-3 px-4 text-center">
                <button
                  type="button"
                  class="flex size-7 mx-auto items-center justify-center rounded-lg bg-woot-500 hover:bg-woot-600 text-[#1c1c1c] shadow-2xs transition-colors cursor-pointer"
                  title="Abrir Atendimento"
                  @click="openConversation(conversation)"
                >
                  <Icon icon="i-lucide-arrow-right" class="size-4" />
                </button>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="conversations.length === 0">
              <td
                colspan="9"
                class="py-12 text-center text-n-slate-10 font-medium"
              >
                <Icon
                  icon="i-lucide-inbox"
                  class="mx-auto mb-2 size-8 opacity-40"
                />
                <span>Nenhum negócio/lead encontrado com os filtros atuais.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </main>
</template>
